import React from "react";
import { Link } from "react-router-dom";
import { Plane, Package, MapPin, Weight, Clock, TrendingUp } from "lucide-react";
import { Rating, VerifiedBadge, StatusBadge } from "./common";
import { rupiah, fmtDate } from "../lib/format";
import { useI18n } from "../lib/i18n";

export function DestinationCard({ dest, onClick }) {
  return (
    <button onClick={onClick} data-testid={`destination-card-${dest.code}`}
      className="group relative overflow-hidden rounded-3xl text-left w-full h-48 shadow-soft hover:shadow-lift transition-transform duration-200 hover:-translate-y-1">
      <img src={dest.image} alt={dest.name} loading="lazy"
        className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
        <div className="text-2xl mb-1">{dest.flag}</div>
        <div className="font-heading font-extrabold text-xl leading-tight">{dest.name}</div>
        <div className="text-lime text-sm font-semibold mt-0.5">
          {dest.bawainer_count ?? 0} BAWAINER
        </div>
      </div>
    </button>
  );
}

export function BawainerCard({ trip, onRequest }) {
  const { t } = useI18n();
  const b = trip.bawainer || {};
  return (
    <div data-testid={`bawainer-card-${trip.id}`}
      className="relative bg-white rounded-3xl p-5 shadow-soft hover:shadow-lift transition-transform duration-200 hover:-translate-y-1 border border-black/5">
      {trip.boosted && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-lime text-navy rounded-full px-2 py-0.5">
          <TrendingUp size={11} /> Boosted
        </span>
      )}
      <Link to={`/bawainer/${b.id}`} className="flex items-center gap-3">
        <img src={b.avatar} alt={b.name} className="w-14 h-14 rounded-2xl object-cover" />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-heading font-bold text-ink truncate">{b.name}</span>
            <VerifiedBadge status={b.verified_status} showLabel={false} />
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
            <Rating value={b.rating} />
            <span>{b.completed_trips} {t("completed_trips")}</span>
          </div>
        </div>
      </Link>

      <div className="mt-4 flex items-center gap-2 text-sm font-semibold text-navy">
        <MapPin size={15} className="text-sky" />
        {trip.from_city} → {trip.to_city}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1"><Plane size={13} /> {fmtDate(trip.departure_date)}</div>
        <div className="flex items-center gap-1"><Weight size={13} /> {trip.capacity_kg} kg</div>
        <div className="flex items-center gap-1"><Clock size={13} /> {b.response_rate}% resp</div>
        <div className="flex items-center gap-1 font-semibold text-ink">{rupiah(trip.service_fee)}/item</div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1">
        {(trip.categories || []).slice(0, 3).map((c) => (
          <span key={c} className="text-[11px] bg-cloud rounded-full px-2 py-0.5 text-navy font-medium">{c}</span>
        ))}
      </div>

      {onRequest && (
        <button onClick={() => onRequest(trip)} data-testid={`request-item-btn-${trip.id}`}
          className="mt-4 w-full rounded-full bg-lime hover:bg-lime-dark text-navy font-bold py-2.5 text-sm transition-colors">
          {t("request_item")}
        </button>
      )}
    </div>
  );
}

export function TripCard({ trip, footer }) {
  return (
    <div data-testid={`trip-card-${trip.id}`} className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading font-bold text-navy">
          <MapPin size={16} className="text-sky" /> {trip.from_city} → {trip.to_city}
        </div>
        <StatusBadge status={trip.status} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1"><Plane size={13} /> {fmtDate(trip.departure_date)}</div>
        <div className="flex items-center gap-1"><Weight size={13} /> {trip.capacity_kg} kg</div>
        <div className="font-semibold text-ink">{rupiah(trip.service_fee)}/item</div>
      </div>
      {footer}
    </div>
  );
}

export function RequestCard({ req, action }) {
  return (
    <div data-testid={`request-card-${req.id}`} className="bg-white rounded-3xl p-5 shadow-soft border border-black/5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs text-sky font-semibold flex items-center gap-1">
            <MapPin size={12} /> {req.destination}
          </div>
          <div className="font-heading font-bold text-ink mt-1">{req.product_name}</div>
        </div>
        <div className="w-11 h-11 rounded-xl bg-cloud flex items-center justify-center shrink-0">
          <Package size={20} className="text-navy" />
        </div>
      </div>
      {req.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{req.description}</p>}
      <div className="mt-3 flex items-center justify-between">
        <span className="text-sm font-bold text-ink">{rupiah(req.budget)}</span>
        <StatusBadge status={req.status} />
      </div>
      {action}
    </div>
  );
}
